/**
 * Plausibilidad del bruto (principio 5 del análisis: avisar, no bloquear).
 * Rangos de brutos REALES 2025 para 1 y 2 carros; para N>=3 no hay datos
 * (en 2025 no hubo viajes de más de 2 carros) y se extrapola N x rango de 1,
 * documentado como hipótesis de UI. Solo lo físicamente imposible bloquea:
 * pesar menos que los carros vacíos, o más que el techo que el servidor acepta.
 * El servidor NUNCA valida plausibilidad; el techo sí (§5.2.2, `payload_invalido`).
 */

import { MAX_BRUTO_G } from '../../src/sync/validacion';

const RANGO_KG: Record<number, [number, number]> = {
	1: [3000, 7600],
	2: [6800, 14800],
};

/** El techo del servidor, en kilos: por encima la mutación vuelve rechazada con `payload_invalido`. */
export const TECHO_BRUTO_KG = MAX_BRUTO_G / 1000;

export function rangoPlausibleKg(cantidadCarros: number): [number, number] {
	return RANGO_KG[cantidadCarros] ?? [3000 * cantidadCarros, 7600 * cantidadCarros];
}

/**
 * Por qué un bruto no puede ser: no cubre los carros vacíos (`taras`) o supera el techo del
 * servidor (`techo`; Codex, ronda del 6/10: el pad admite seis cifras y, sin el «Sí, está bien»
 * de por medio, un 100.001 llegaba a la cola y volvía rechazado). Null si es posible.
 */
export function motivoImposible(brutoKg: number, sumaTarasG: number): 'taras' | 'techo' | null {
	if (brutoKg * 1000 <= sumaTarasG) return 'taras';
	if (brutoKg > TECHO_BRUTO_KG) return 'techo';
	return null;
}

/** Bloqueo duro: el bruto no llega a cubrir los carros vacíos o supera el techo del servidor. */
export function esImposible(brutoKg: number, sumaTarasG: number): boolean {
	return motivoImposible(brutoKg, sumaTarasG) !== null;
}

/** Aviso blando: fuera del rango histórico para esa cantidad de carros. */
export function esRaro(brutoKg: number, cantidadCarros: number): boolean {
	const [minimo, maximo] = rangoPlausibleKg(cantidadCarros);
	return brutoKg < minimo || brutoKg > maximo;
}

export function netoG(brutoKg: number, sumaTarasG: number): number {
	return brutoKg * 1000 - sumaTarasG;
}
